import { AggregationFunction, type OutputColumn } from "services/adi/types";

/**
 * Normalizes a team ID for use in ADI API calls.
 * Converts to lowercase, replaces spaces and special characters with hyphens.
 */
export const normalizeTeamId = (teamId: string | undefined | null): string => {
  if (!teamId) {
    return "";
  }
  return teamId.toLowerCase().replace(/[^a-z0-9-]/g, "-");
};

/**
 * Bounds ADI accepts when splitting export/report output files by line count.
 * Below the minimum the split produces an unmanageable number of files; above
 * the maximum the individual files become too large to open.
 */
export const MIN_LINES_PER_FILE = 10_000;
export const MAX_LINES_PER_FILE = 1_000_000;

/**
 * Upper bound quoted to users when sizing a line count, so a dropped digit is
 * recognisable as producing an unreasonable number of files.
 *
 * UI guidance only — no row cap is enforced anywhere in AuditsDataInsights. The
 * largest export observed in the Spark job's own fixtures is ~208M rows.
 */
export const MAX_EXPORT_ROWS = 500_000_000;

/**
 * Lines per file applied when a request omits a line count.
 *
 * Enforced twice: ExportMapper.DEFAULT_LINES_PER_FILE fills it in for exports
 * created through the API, and the Spark job's SplitConfig substitutes it for
 * any absent, empty, or non-positive value. The second path is what gives
 * reports the same default — the report state machine writes linesPerFile
 * straight from exportConfig.splitByLineCount, which is an empty string when
 * unset.
 *
 * A valid positive value is always honoured; only unusable values are replaced.
 */
export const DEFAULT_LINES_PER_FILE = 1_000_000;

/**
 * Guidance shown under the "Splitting options" header on both the export and
 * report wizards. Scoped to the section rather than to either checkbox: the
 * default applies even when file splitting is switched off entirely.
 */
export const splittingOptionsDescription = (): string =>
  `Exports are limited to ${(MAX_EXPORT_ROWS / 1_000_000).toLocaleString()} million rows. ` +
  `Lines per file defaults to ${DEFAULT_LINES_PER_FILE.toLocaleString()}.`;

/**
 * Validates a lines-per-file value taken from a text input.
 *
 * @param value raw input value
 * @returns an error message, or "" when the value is acceptable
 */
export const validateLinesPerFile = (value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Enter the number of lines per file.";
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Lines per file must be a whole number.";
  }
  const parsed = Number(trimmed);
  if (parsed < MIN_LINES_PER_FILE || parsed > MAX_LINES_PER_FILE) {
    return `Lines per file must be between ${MIN_LINES_PER_FILE.toLocaleString()} and ${MAX_LINES_PER_FILE.toLocaleString()}.`;
  }
  return "";
};

/**
 * Builds the ordered output columns for a report.
 *
 * `timeColumn` is force-included as an ATTRIBUTE group-by dimension when the user
 * has not selected it, because the API requires it and because the report's own
 * export splits its files on it.
 *
 * That second reason is why the split column must be the time column and nothing
 * else. The report state machine writes the EXPORT# record straight from Step
 * Functions and never sets a column list, so the export job reads the report
 * table's columns as-is; a split column absent from that table fails the export
 * with "splitDateColumn '<name>' is not present in the exported columns". Adding
 * some other date column here would fix that but make it a second GROUP BY key,
 * changing the report's rows and how its measures divide.
 *
 * A column the user already selected keeps its chosen aggregation and position.
 */
export const buildReportOutputColumns = ({
  selectedColumns,
  aggregations,
  timeColumn,
}: {
  selectedColumns: string[];
  aggregations: Record<string, AggregationFunction>;
  timeColumn: string;
}): OutputColumn[] => {
  const cols: OutputColumn[] = selectedColumns.map((name) => ({
    columnName: name,
    // The time column is always a dimension, never a measure. A measure is
    // aliased "<name>_<fn>" in the report output, which would rename the very
    // column the export splits on and fail the export.
    function:
      name === timeColumn
        ? AggregationFunction.ATTRIBUTE
        : aggregations[name] || AggregationFunction.ATTRIBUTE,
  }));

  if (timeColumn && !cols.some((c) => c.columnName === timeColumn)) {
    cols.unshift({
      columnName: timeColumn,
      function: AggregationFunction.ATTRIBUTE,
    });
  }

  return cols;
};
